/**
 * Token obfuscation utilities for SignalR authentication
 * Uses simple XOR obfuscation for mobile platforms only
 */

// XOR key - should match the key used on the server for de-obfuscation
const XOR_KEY = 'GrokioSignalR2024';

/**
 * Obfuscates a token using XOR cipher
 * @param token - The original auth token
 * @returns The obfuscated token as a base64 string, or original token for web
 */
export const obfuscateToken = (token: string): string => {
  if (!token) {
    return token;
  }

  try {
    const tokenBytes = new TextEncoder().encode(token);
    const keyBytes = new TextEncoder().encode(XOR_KEY);
    const obfuscated = new Uint8Array(tokenBytes.length);

    // XOR each byte of the token with the corresponding key byte (cycling through key)
    for (let i = 0; i < tokenBytes.length; i++) {
      obfuscated[i] = tokenBytes[i] ^ keyBytes[i % keyBytes.length];
    }

    // Convert to base64 for safe transmission
    return btoa(String.fromCharCode.apply(null, Array.from(obfuscated)));
  } catch (error) {
    console.error('Error obfuscating token:', error);
    return token; // Return original token if obfuscation fails
  }
};

/**
 * De-obfuscates a token using XOR cipher (for testing purposes)
 * @param obfuscatedToken - The obfuscated token as base64 string
 * @returns The original token
 */
export const deobfuscateToken = (obfuscatedToken: string): string => {
  if (!obfuscatedToken) {
    return obfuscatedToken;
  }

  try {
    // Convert from base64
    const obfuscatedBytes = new Uint8Array(
      atob(obfuscatedToken).split('').map(char => char.charCodeAt(0))
    );
    const keyBytes = new TextEncoder().encode(XOR_KEY);
    const deobfuscated = new Uint8Array(obfuscatedBytes.length);

    // XOR each byte with the corresponding key byte to reverse the obfuscation
    for (let i = 0; i < obfuscatedBytes.length; i++) {
      deobfuscated[i] = obfuscatedBytes[i] ^ keyBytes[i % keyBytes.length];
    }

    return new TextDecoder().decode(deobfuscated);
  } catch (error) {
    console.error('Error de-obfuscating token:', error);
    return obfuscatedToken; // Return original if de-obfuscation fails
  }
}; 